<template>
  <div class="SalesReportItem">
    <div class="cell col1">
      <template v-if="item.membership">
        <div class="gender">
          <GenderAge
            :birthdate="item.membership.customer.birthdate"
            :gender="item.membership.customer.gender" />
        </div>
      </template>
    </div>
    <div class="cell col2">
      <label>Member</label>
      <MemberAutocompleteInput
        v-model="item.membership"
        @input="memberChanged" />
    </div>
    <div class="cell col3">
      <label>Type of customer</label>
      <select
        :disabled="readOnly"
        @change="updateItem"
        v-model="item.customerType"
        required>
        <option value="NEWSALE">
          Nysalgskunde
        </option>
        <option value="RESALE">
          Resalgskunde
        </option>
        <option value="PORTFOLIO">
          Porteføljekunde
        </option>
        <option value="CAMPAIGN">
          Kampanjekunde
        </option>
      </select>
    </div>
    <div class="cell col4">
      <label>Location</label>
      <select
        :disabled="readOnly"
        @change="updateItem"
        v-model="item.location">
        <option
          disabled
          :value="null">
          Please select...
        </option>
        <option
          v-for="location of locationsList"
          :key="location.id"
          :value="location">
          {{ location.name }}
        </option>
      </select>
    </div>
    <div class="cell col5">
      <label>Amount</label>
      <input
        :disabled="readOnly"
        @change="updateItem"
        @keyup="updateItem"
        type="number"
        v-model.number="item.amount">
    </div>
    <div class="cell col6">
      <div
        v-if="!readOnly"
        class="delete"
        @click="deleteItem()">
        <Icon
          name="trash"
          @click="deleteItem()" />
        <span class="label">Delete</span>
      </div>
      <div
        v-if="!readOnly"
        class="save">
        <IconButton
          name="send"
          @click="save()">
          Save
        </IconButton>
      </div>
    </div>
  </div>
</template>

<script>
import gql from "graphql-tag";
import debounce from "lodash/debounce";
import MemberAutocompleteInput from "@/components/MemberAutocompleteInput";
import GenderAge from "@/components/GenderAge";

export default {
  components: { MemberAutocompleteInput, GenderAge },
  props: {
    item: {
      type: Object,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },

  mounted() {},

  apollo: {
    locationsList: {
      query: gql`
        query LocationListQuery {
          locationsList(orderBy: NAME_ASC) {
            name
            id
          }
        }
      `,
    },
  },

  methods: {
    save() {
      this.updateItem().then(() => this.$emit("saved", this.item));
    },

    memberChanged() {
      if (this.item.membership === null) {
        return Promise.resolve();
      }
      if (this.item.membership.location) {
        const ll = this.locationsList.filter(
          (l) => l.id === this.item.membership.location.id
        );
        if (ll.length > 0) {
          this.item.location = ll[0];
        }
      }
      if (this.item.membership.id) {
        return this.updateItem();
      }
      return Promise.resolve();
    },

    updateItem: debounce(
      function () {
        const item = this.item;
        if (
          !(
            item.id &&
            (item.amount || item.amount === 0) &&
            item.membership &&
            item.membership.id &&
            item.customerType &&
            item.location &&
            item.location.id
          )
        ) {
          return Promise.resolve();
        }
        return this.$apollo
          .mutate({
            mutation: gql`
              mutation UpdateSalesReportItemMutation(
                $id: Int!
                $amount: BigFloat!
                $customerType: SalesReportCustomerType!
                $locationId: UUID!
                $membershipId: String!
              ) {
                updateSalesReportItem(
                  input: {
                    patch: {
                      amount: $amount
                      customerType: $customerType
                      locationId: $locationId
                      membershipId: $membershipId
                    }
                    id: $id
                  }
                ) {
                  clientMutationId
                }
              }
            `,
            variables: {
              id: item.id,
              amount: item.amount,
              membershipId: item.membership.id,
              customerType: item.customerType,
              locationId: item.location.id,
            },
          })
          .then((response) => {
            this.$emit("sales-report-item-updated", response);
          })
          .catch((err) => {
            console.error("failed to save report item", err);
          });
      },
      500,
      { leading: true }
    ),

    deleteItem() {
      const item = this.item;
      this.$apollo
        .mutate({
          mutation: gql`
            mutation DeleteSalesReportItem($id: Int!) {
              updateSalesReportItem(
                input: {
                  patch: {
                    deleted: true
                  }
                  id: $id
                }
              ) {
                clientMutationId
              }
            }
          `,
          variables: { id: item.id },
        })
        .then((response) => {
          this.$emit("sales-report-item-deleted", response);
        })
        .catch((err) => {
          console.error("failed to delete report item", err);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/responsive.scss";

.SalesReportItem {
  display: flex;
  flex: 1;
  align-items: center;
  border-bottom: solid 1px var(--heather);
  padding-bottom: 1em;
  @include phone {
    flex-direction: column;
  }

  .gender {
    width: 3em;
  }

  .cell {
    display: flex;
    padding: 1em;
    padding-left: 0;
    @include phone {
      width: 100% !important;
      display: flex;
      flex-direction: column;
    }

    label {
      display: none;
      @include phone {
        display: inherit;
        flex: 1;
      }
    }
  }

  .col1 {
    width: 10%;
    justify-content: center;
    @include phone {
      display: none;
    }
  }

  .col2 {
    width: 25%;
  }

  .col3 {
    width: 20%;
  }

  .col4 {
    width: 20%;
  }

  .col5 {
    width: 20%;
    input {
      flex: auto;
      @include phone {
        flex: 1;
        width: 100%;
      }
    }
  }

  .col6 {
    width: 10%;
    flex-direction: row;
    align-items: center;
    padding-left: 2em;
    @include phone {
      border-top: solid 1px var(--heather);
    }
    .save {
      display: none;
      @include phone {
        display: inherit;
      }
    }
    .delete {
      .label {
        display: none;
        @include phone {
          display: inherit;
          font-weight: bold;
        }
      }
      flex: 1;
      display: flex;
    }
  }

  .delete {
    cursor: pointer;
    opacity: 0.5;
    text-align: center;
  }
}
</style>
